/**
 * groupJobTasks — one job's Production Detail rows, grouped the way the shop
 * reads them (SO page, 2026-09-08, Mike / SO-1119 job 9537).
 *
 * GET /jobs/{id}/progress returns one row per job_tasks record in task_id
 * order. Honest, and unreadable on a mixed sheet: job 9537 was one shared laser
 * task feeding three parts, two per-part chains laid end to end, six per-set
 * twin rows (352-2579 builds two sets of 526-3105 SUPPORT AS, so Welding,
 * Coating and Shipping each existed twice at qty 1), a laser-only plate
 * (526-3104) with no row at all, and a defect stored without a task link that
 * rendered five rows away from the Brake row it belonged to.
 *
 * This turns that list into:
 *   shared   — part-less tasks (the laser sheet / saw list), first, in task order
 *   groups   — one per part, in order of first appearance among the tasks, then
 *              the parts known only from a shared task's `parts[]` (laser-only /
 *              list-only parts, consumed at a join). jobq-api fills `parts[]` for
 *              sheet tasks since 2026-09-08; older payloads simply produce no
 *              shared-only groups and no "feeds" list.
 *     steps  — the part's stations in chain order (creation order follows the
 *              routing), Shipping pinned last. CONSECUTIVE tasks with the same
 *              (workstation, sub_process) fold into one step with `sets` = task
 *              count; per-set rows are labelled by set_identifier when present.
 *              Cancelled / Voided tasks stay visible as their own terminal step
 *              and never fold.
 *     defects— decoupled defects (task_id NULL) attach to the part's step at that
 *              workstation, else to the part; consumable defects with no part
 *              fall through to `unmatchedDefects`.
 * Pure: no React, no fetch. Badges and pace stay in SalesOrderPage.
 */

const TERMINAL = new Set(["Cancelled", "Voided"]);
const ACTIVE = new Set(["In Progress", "Paused"]);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "H:MM:SS" / "MM:SS" / seconds → seconds. Mirrors TimeComparison on the SO page. */
export function parseSeconds(val) {
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (!val) return 0;
  const parts = String(val).split(":").map(Number);
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

export function stepKey(t) {
  return `${t.workstation || ""}|${t.sub_process || ""}`;
}

export function stepLabel(t) {
  return t.sub_process ? `${t.workstation} (${t.sub_process})` : `${t.workstation || ""}`;
}

/** Label for one task inside a folded step. The API's set_identifier wins; else ordinal. */
export function setLabel(task, index) {
  return task && task.set_identifier != null ? `Set ${task.set_identifier}` : `Set ${index + 1}`;
}

function newStep(t) {
  return {
    key: stepKey(t),
    workstation: t.workstation,
    sub_process: t.sub_process || null,
    label: stepLabel(t),
    tasks: [t],
    terminal: TERMINAL.has(t.status),
    defectRows: [],
  };
}

function finishStep(step) {
  const tasks = step.tasks;
  const n = tasks.length;
  const done = tasks.filter((t) => t.status === "Completed").length;
  step.taskIds = tasks.map((t) => t.task_id);
  step.sets = n > 1 ? n : 0;
  step.setsDone = done;
  if (step.terminal) step.status = tasks[0].status;
  else if (done === n) step.status = "Completed";
  else if (tasks.some((t) => ACTIVE.has(t.status))) step.status = "In Progress";
  else if (done > 0) step.status = "Partial";
  else step.status = tasks[0].status || "Pending";
  step.quantity = tasks.reduce((a, t) => a + num(t.quantity), 0);
  step.qty_completed = tasks.reduce((a, t) => a + num(t.qty_completed), 0);
  step.defect_quantity = tasks.reduce((a, t) => a + num(t.defect_quantity), 0);
  step.blocked = tasks.some((t) => t.blocked && t.status !== "Completed");
  step.scheduled_date = tasks.map((t) => t.scheduled_date).filter(Boolean).sort()[0] || null;
  step.estimated_seconds = tasks.reduce((a, t) => a + parseSeconds(t.estimated_time), 0);
  step.elapsed_seconds = tasks.reduce((a, t) => a + num(t.elapsed_time_seconds), 0);
  step.start_time = tasks.map((t) => t.start_time).filter(Boolean).sort()[0] || null;
  step.end_time = tasks.map((t) => t.end_time).filter(Boolean).sort().slice(-1)[0] || null;
  const withUser = tasks
    .filter((t) => t.last_user_name)
    .sort((a, b) => String(b.end_time || b.start_time || "").localeCompare(String(a.end_time || a.start_time || "")));
  step.last_user_name = withUser.length ? withUser[0].last_user_name : null;
  return step;
}

export function groupJobTasks(job) {
  const tasks = ((job && job.tasks) || []).slice().sort((a, b) => num(a.task_id) - num(b.task_id));
  const shared = tasks.filter((t) => t.part_id == null);
  const groups = new Map();

  const ensure = (partId, seed) => {
    if (!groups.has(partId)) {
      groups.set(partId, {
        part_id: partId,
        product_id: seed.product_id || null,
        description: seed.description || null,
        tasks: [],
        sharedTasks: [],
        sheetQty: 0,
        steps: [],
        looseDefects: [],
        defectCount: 0,
        joinsProductId: null,
        joinWorkstation: null,
        componentOfAssembly: null,
      });
    }
    const g = groups.get(partId);
    if (!g.product_id && seed.product_id) g.product_id = seed.product_id;
    if (!g.description && seed.description) g.description = seed.description;
    return g;
  };

  tasks
    .filter((t) => t.part_id != null)
    .forEach((t) => ensure(t.part_id, { product_id: t.product_id, description: t.part_description }).tasks.push(t));

  shared.forEach((s) => {
    (Array.isArray(s.parts) ? s.parts : []).forEach((p) => {
      if (!p || p.part_id == null) return;
      const g = ensure(p.part_id, { product_id: p.product_id, description: p.description });
      if (!g.sharedTasks.includes(s)) g.sharedTasks.push(s);
      g.sheetQty += num(p.quantity);
      if (p.joins_product_id && !g.joinsProductId) g.joinsProductId = p.joins_product_id;
      if (p.join_workstation && !g.joinWorkstation) g.joinWorkstation = p.join_workstation;
      if (p.component_of_assembly && !g.componentOfAssembly) g.componentOfAssembly = p.component_of_assembly;
    });
  });

  groups.forEach((g) => {
    const steps = [];
    g.tasks.forEach((t) => {
      const last = steps[steps.length - 1];
      if (last && !last.terminal && !TERMINAL.has(t.status) && last.key === stepKey(t)) last.tasks.push(t);
      else steps.push(newStep(t));
    });
    const shipping = steps.filter((s) => s.workstation === "Shipping");
    const rest = steps.filter((s) => s.workstation !== "Shipping");
    g.steps = rest.concat(shipping).map(finishStep);
    g.qty = g.sheetQty || g.tasks.reduce((m, t) => Math.max(m, num(t.quantity)), 0);
    g.sharedOnly = g.tasks.length === 0;
    g.sharedKind = g.sharedTasks.some((s) => s.cut_sheet_id != null) ? "sheet" : g.sharedTasks.length ? "list" : null;
    g.defectCount = g.steps.reduce((a, s) => a + s.defect_quantity, 0);
  });

  const unmatchedDefects = [];
  ((job && job.decoupled_defects) || []).forEach((d) => {
    const g = d && d.part_id != null ? groups.get(d.part_id) : null;
    if (!g) {
      unmatchedDefects.push(d);
      return;
    }
    const step = g.steps.find((s) => !s.terminal && s.workstation === d.workstation);
    if (step) step.defectRows.push(d);
    else g.looseDefects.push(d);
    g.defectCount += num(d.defective_quantity);
  });

  // Parts with stations first (in first-appearance order), shared-only parts after.
  const ordered = [...groups.values()].sort((a, b) => (a.sharedOnly === b.sharedOnly ? 0 : a.sharedOnly ? 1 : -1));
  ordered.forEach((g) => {
    const live = g.steps.filter((s) => !s.terminal);
    const sharedDone = g.sharedTasks.length > 0 && g.sharedTasks.every((s) => s.status === "Completed");
    g.stepsTotal = (g.sharedTasks.length ? 1 : 0) + live.length;
    g.stepsDone = (sharedDone ? 1 : 0) + live.filter((s) => s.status === "Completed").length;
    g.next = live.find((s) => s.status !== "Completed") || null;
  });

  const sheets = [...new Set(shared.map((s) => s.cut_sheet_id).filter((x) => x != null))];
  const lists = [...new Set(shared.map((s) => s.cut_list_id).filter((x) => x != null))];
  return {
    shared,
    groups: ordered,
    unmatchedDefects,
    summary: {
      sheets,
      lists,
      partCount: ordered.length,
      taskCount: tasks.filter((t) => !TERMINAL.has(t.status)).length,
      next: earliestNext(ordered),
    },
  };
}

/** The soonest-scheduled next step across part groups (unscheduled ones lose to dated ones). */
function earliestNext(groups) {
  let next = null;
  groups.forEach((g) => {
    if (!g.next) return;
    if (!next) {
      next = g.next;
      return;
    }
    const a = g.next.scheduled_date;
    const b = next.scheduled_date;
    if (a && (!b || a < b)) next = g.next;
  });
  return next;
}

/**
 * What the order being shown owns, for scoping a job's part blocks to it
 * (2026-10-23, Mike, SO-1141 / job #10304). A laser sheet carries parts for
 * several orders: job #10304 is SO-1138's (saddle brackets ×81) and also cut
 * SO-1141's three plates, which reach SO-1141 by surplus allocation. SO-1141's
 * Production Detail drew the whole job, SO-1138's 3 Brake defects included.
 *
 *   partIds   — every part on the order's /truth lines (items, assembly
 *               components and primaries), as strings
 *   elsewhere — part_id → order numbers a part the order's own job made was
 *               allocated to (/truth orphan_productions: on SO-1138, the
 *               plates → SO-1141)
 */
export function orderScope(truth) {
  const partIds = new Set();
  const add = (id) => {
    if (id != null) partIds.add(String(id));
  };
  ((truth && truth.lines) || []).forEach((entry) => {
    if (!entry) return;
    if (entry.type === "assembly_group") {
      add(entry.primary_part_id);
      (entry.components || []).forEach((c) => c && add(c.part_id));
    } else if (entry.type === "item" && entry.data) {
      add(entry.data.part_id);
    } else {
      add(entry.part_id);
    }
  });
  const elsewhere = {};
  ((truth && truth.orphan_productions) || []).forEach((o) => {
    if (!o || o.part_id == null) return;
    const sos = [...new Set((o.outgoing_allocations || []).map((a) => a && a.target_so_order_number).filter(Boolean))];
    if (sos.length) elsewhere[String(o.part_id)] = sos;
  });
  return { partIds, elsewhere };
}

/**
 * The orders a job's part belongs to when it is NOT the shown order's, else null
 * (the part stays in view). A part is moved out only on evidence it is another
 * order's: allocated to one (scope.elsewhere), or on a job that is itself another
 * order's (job._source "allocated"). A part on the order's own job that its lines
 * do not name (a sub-assembly, stock surplus) stays, as before.
 */
export function partOwnersElsewhere(partId, job, scope) {
  if (!scope || scope.partIds.size === 0 || partId == null) return null;
  const key = String(partId);
  if (scope.partIds.has(key)) return null;
  if (scope.elsewhere[key]) return scope.elsewhere[key];
  if (job && job._source === "allocated") return [job._source_so_number || "another order"];
  return null;
}

/**
 * groupJobTasks output narrowed to the shown order: `groups` = its parts,
 * `others` = the rest with their owning orders, `otherOrders` = those orders
 * once each, `summary` recounted over the shared tasks + its parts. When nothing
 * on the job is the order's (should not happen), the job is shown whole.
 */
export function scopeJobToOrder(grouped, job, scope) {
  const mine = [];
  const others = [];
  grouped.groups.forEach((g) => {
    const owners = partOwnersElsewhere(g.part_id, job, scope);
    if (owners) others.push({ group: g, owners });
    else mine.push(g);
  });
  if (others.length === 0 || mine.length === 0) {
    return { ...grouped, others: [], otherOrders: [] };
  }
  const live = (t) => !TERMINAL.has(t.status);
  return {
    ...grouped,
    groups: mine,
    others,
    otherOrders: [...new Set(others.flatMap((o) => o.owners))],
    summary: {
      ...grouped.summary,
      partCount: mine.length,
      taskCount: grouped.shared.filter(live).length + mine.reduce((a, g) => a + g.tasks.filter(live).length, 0),
      next: earliestNext(mine),
    },
  };
}

/** part_id → { assembly_name, sets, isPrimary } from /truth's assembly_group lines. */
export function assemblyInfoByPart(truthLines) {
  const out = {};
  (truthLines || []).forEach((entry) => {
    if (!entry || entry.type !== "assembly_group") return;
    const sets = entry.sets != null ? num(entry.sets) : num(entry.quantity_ordered);
    (entry.components || []).forEach((c) => {
      if (!c || c.part_id == null || out[c.part_id]) return;
      out[c.part_id] = { assembly_name: entry.assembly_name, sets, isPrimary: c.part_id === entry.primary_part_id };
    });
  });
  return out;
}

/** part_id → [sales_order_line_id, ...] from flattened /truth lines. */
export function soLinesByPart(flatLines) {
  const out = {};
  (flatLines || []).forEach((l) => {
    if (!l || l.part_id == null || l.sales_order_line_id == null) return;
    (out[l.part_id] = out[l.part_id] || []).push(l.sales_order_line_id);
  });
  return out;
}
